//AdminFrontend/src/admin_ui/pages/AdminPosInventoryApprovalsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  listPosInventorySubmissions,
  approvePosInventorySubmission,
  rejectPosInventorySubmission,
} from "../services/adminPosInventoryApi";
import "../styles/Admin.css";

export default function AdminPosInventoryApprovalsPage() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [noteById, setNoteById] = useState({});

  // real-time filters (client-side)
  const [qName, setQName] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [qtyBucket, setQtyBucket] = useState(""); // "", "le10", "le30", ... "ge100"

  function n(v, def = 0) {
    const x = Number(v);
    return Number.isFinite(x) ? x : def;
  }
  function fmtMoney(v) {
    return n(v, 0).toFixed(2);
  }
  function fmtTaxRate(r) {
    // stored as fraction: 0.15 -> 15%
    return `${(n(r, 0) * 100).toFixed(2)}%`;
  }
  function gross(unitPrice, taxRate) {
    return n(unitPrice, 0) * (1 + n(taxRate, 0));
  }

  async function load() {
    setErr("");
    setLoading(true);
    try {
      const res = await listPosInventorySubmissions({ status, limit: 50, offset: 0 });
      setRows(res?.rows || []);
    } catch (e) {
      setErr("Unable to load POS inventory submissions.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const filteredRows = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    const nameNeedle = String(qName || "").trim().toLowerCase();
    const minP = minPrice === "" ? null : Number(minPrice);
    const maxP = maxPrice === "" ? null : Number(maxPrice);

    function qtyOk(qty) {
      const q = Number(qty || 0);
      if (!Number.isFinite(q)) return false;
      if (!qtyBucket) return true;
      if (qtyBucket === "le10") return q <= 10;
      if (qtyBucket === "le30") return q <= 30;
      if (qtyBucket === "le50") return q <= 50;
      if (qtyBucket === "le70") return q <= 70;
      if (qtyBucket === "le90") return q <= 90;
      if (qtyBucket === "ge100") return q >= 100;
      return true;
    }

    return list.filter((r) => {
      const nm = String(r?.name || "").toLowerCase();
      if (nameNeedle && !nm.includes(nameNeedle)) return false;

      const p = n(r?.unit_price ?? r?.unitPrice ?? 0, 0);
      if (minP !== null && Number.isFinite(minP) && p < minP) return false;
      if (maxP !== null && Number.isFinite(maxP) && p > maxP) return false;

      const qty = r?.initial_quantity ?? r?.quantity ?? 0;
      if (!qtyOk(qty)) return false;

      return true;
    });
  }, [rows, qName, minPrice, maxPrice, qtyBucket]);

  async function onApprove(id) {
    setErr("");
    try {
      const note = String(noteById[id] || "").trim();
      const payload = note ? { reviewNote: note } : {};
      await approvePosInventorySubmission(id, payload);
      await load();
    } catch (e) {
      setErr("Approve failed.");
    }
  }

  async function onReject(id) {
    setErr("");
    try {
      const note = (noteById[id] || "").trim();
      if (!note) {
        setErr("Reject requires a note.");
        return;
      }
      await rejectPosInventorySubmission(id, { reviewNote: note });
      await load();
    } catch (e) {
      setErr("Reject failed.");
    }
  }

  return (
    <section className="ucx-section">
      <div className="ucx-container ucx-admin-page">
        <div className="ucx-admin-header">
          <div>
            <h1 className="ucx-admin-title">POS Inventory Approvals</h1>
            <p className="ucx-admin-subtitle">Approve products submitted by pos_inventory_clerk.</p>
          </div>

          <div className="ucx-admin-header-right">
            <div className="ucx-admin-header-actions">
              <button type="button" className="ucx-btn ucx-btn-outline" onClick={load} disabled={loading}>
                {loading ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </div>
        </div>

        {err ? <div className="ucx-admin-status-error">{err}</div> : null}

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Filters</div>
          <div className="ucx-admin-filters ucx-admin-filters-inventory">
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>

            <input
              value={qName}
              onChange={(e) => setQName(e.target.value)}
              placeholder="Search name…"
            />

            <input
              type="number"
              min="0"
              step="0.01"
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
              placeholder="Min price"
            />

            <input
              type="number"
              min="0"
              step="0.01"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              placeholder="Max price"
            />

            <select value={qtyBucket} onChange={(e) => setQtyBucket(e.target.value)}>
              <option value="">All quantities</option>
              <option value="le10">Qty ≤ 10</option>
              <option value="le30">Qty ≤ 30</option>
              <option value="le50">Qty ≤ 50</option>
              <option value="le70">Qty ≤ 70</option>
              <option value="le90">Qty ≤ 90</option>
              <option value="ge100">Qty ≥ 100</option>
            </select>

            <span className="ucx-small">
              Showing {filteredRows.length} of {(Array.isArray(rows) ? rows.length : 0)}
            </span>
          </div>
        </div>

        <div className="ucx-admin-table-wrapper">
          <table className="ucx-admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Branch</th>
                <th>Submitted by</th>
                <th>Name</th>
                <th>Tax</th>
                <th>Price</th>
                <th>Gross</th>
                <th>Qty</th>
                <th>Status</th>
                <th style={{ width: 340 }}>Review note</th>
                <th style={{ width: 220 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={11} className="ucx-admin-empty">Loading submissions…</td>
                </tr>
              )}

              {!loading && filteredRows.length === 0 && (
                <tr>
                  <td colSpan={11} className="ucx-admin-empty">No records.</td>
                </tr>
              )}

              {!loading &&
                filteredRows.map((r) => (
                  <tr key={r.id}>
                    <td>{r.id}</td>
                    <td>{r.branch_name || r.branch_id}</td>
                    <td>{r.submitted_by_email || r.submitted_by_user_id}</td>
                    <td>{r.name}</td>
                    <td>{fmtTaxRate(r.tax_rate ?? r.taxRate)}</td>
                    <td>{fmtMoney(r.unit_price ?? r.unitPrice)}</td>
                    <td>{fmtMoney(gross(r.unit_price ?? r.unitPrice, r.tax_rate ?? r.taxRate))}</td>
                    <td>{r.initial_quantity}</td>
                    <td>{r.status}</td>
                    <td>
                      <input
                        className="ucx-admin-input-compact"
                        value={noteById[r.id] || r.review_note || ""}
                        onChange={(e) => setNoteById((m) => ({ ...m, [r.id]: e.target.value }))}
                        placeholder="Optional note (required for reject)"
                      />
                    </td>
                    <td>
                      {r.status === "pending" ? (
                        <div className="ucx-admin-actions">
                          <button
                            type="button"
                            className="ucx-admin-btn ucx-admin-btn-approve"
                            onClick={() => onApprove(r.id)}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="ucx-admin-btn ucx-admin-btn-reject"
                            onClick={() => onReject(r.id)}
                          >
                            Reject
                          </button>
                        </div>
                      ) : (
                        <span className="ucx-admin-empty">—</span>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
